import React, { useEffect, useState } from "react";
import { webhookService } from "@/services/webhookService";
import { WebhookConfig, CreateWebhookRequest, WebhookDelivery } from "@/types/webhook";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { formatLocalDateTime } from '@/lib/date';

const PROJECT_ID = "default"; // TODO: Replace with actual projectId from context or user

const EVENT_OPTIONS = [
  { label: "Add Memory", value: "MemoryAdded" },
  { label: "Update Memory", value: "MemoryUpdated" },
  { label: "Delete Memory", value: "MemoryDeleted" },
];

function getErrorMessage(e: unknown, fallback: string) {
  if (typeof e === "object" && e && "message" in e && typeof (e as { message?: unknown }).message === "string") {
    return (e as { message: string }).message;
  }
  return fallback;
}

const Webhooks: React.FC = () => {
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    url: "",
    eventTypes: ["MemoryAdded"],
  });
  const [submitting, setSubmitting] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; webhook?: WebhookConfig }>({ open: false });
  const [editForm, setEditForm] = useState({ name: "", url: "", eventTypes: [], isActive: true });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [historyModal, setHistoryModal] = useState<{ open: boolean; webhookId?: number }>({ open: false });
  const [deliveryHistory, setDeliveryHistory] = useState<WebhookDelivery[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const data = await webhookService.getWebhooks(PROJECT_ID);
      setWebhooks(data);
    } catch (e: unknown) {
      toast.error(getErrorMessage(e, "Failed to load webhooks"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (eventType: string) => {
    setForm((prev) => {
      const exists = prev.eventTypes.includes(eventType);
      return {
        ...prev,
        eventTypes: exists
          ? prev.eventTypes.filter((et) => et !== eventType)
          : [...prev.eventTypes, eventType],
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.url || form.eventTypes.length === 0) {
      toast.error("Please fill all fields and select at least one event.");
      return;
    }
    setSubmitting(true);
    try {
      const req: CreateWebhookRequest = {
        name: form.name,
        url: form.url,
        eventTypes: form.eventTypes,
      };
      await webhookService.createWebhook(PROJECT_ID, req);
      toast.success("Webhook added!");
      setForm({ name: "", url: "", eventTypes: ["MemoryAdded"] });
      fetchWebhooks();
    } catch (e: unknown) {
      toast.error(getErrorMessage(e, "Failed to add webhook"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this webhook?")) return;
    try {
      await webhookService.deleteWebhook(id);
      toast.success("Webhook deleted");
      fetchWebhooks();
    } catch (e: unknown) {
      toast.error(getErrorMessage(e, "Failed to delete webhook"));
    }
  };

  const openEditModal = (wh: WebhookConfig) => {
    setEditForm({ name: wh.name, url: wh.url, eventTypes: wh.eventTypes, isActive: wh.isActive });
    setEditModal({ open: true, webhook: wh });
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditCheckboxChange = (eventType: string) => {
    setEditForm((prev) => {
      const exists = prev.eventTypes.includes(eventType);
      return {
        ...prev,
        eventTypes: exists
          ? prev.eventTypes.filter((et) => et !== eventType)
          : [...prev.eventTypes, eventType],
      };
    });
  };

  const handleEditSwitchChange = (checked: boolean) => {
    setEditForm((prev) => ({ ...prev, isActive: checked }));
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editModal.webhook) return;
    setEditSubmitting(true);
    try {
      await webhookService.updateWebhook(editModal.webhook.id, {
        name: editForm.name,
        url: editForm.url,
        eventTypes: editForm.eventTypes,
        isActive: editForm.isActive,
      });
      toast.success("Webhook updated!");
      setEditModal({ open: false });
      fetchWebhooks();
    } catch (e: unknown) {
      toast.error(getErrorMessage(e, "Failed to update webhook"));
    } finally {
      setEditSubmitting(false);
    }
  };

  const openHistoryModal = async (webhookId: number) => {
    setHistoryModal({ open: true, webhookId });
    setHistoryLoading(true);
    try {
      const deliveries = await webhookService.getDeliveryHistory(webhookId);
      setDeliveryHistory(deliveries);
    } catch (e: unknown) {
      toast.error(getErrorMessage(e, "Failed to load delivery history"));
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-8">
      <h1 className="text-3xl font-bold mb-2">Webhooks</h1>
      <p className="mb-6 text-muted-foreground">Manage your project's webhooks to receive notifications for various events.</p>
      <div className="bg-card rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Add New Webhook</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1 font-medium">Name</label>
            <Input name="name" value={form.name} onChange={handleInputChange} placeholder="Enter webhook name" />
          </div>
          <div>
            <label className="block mb-1 font-medium">URL</label>
            <Input name="url" value={form.url} onChange={handleInputChange} placeholder="https://your-webhook-url.com" />
          </div>
          <div>
            <label className="block mb-1 font-medium">Events (at least one required)</label>
            <div className="flex gap-8">
              {EVENT_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2">
                  <Checkbox
                    checked={form.eventTypes.includes(opt.value)}
                    onCheckedChange={() => handleCheckboxChange(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Adding..." : "Add Webhook"}
          </Button>
        </form>
      </div>
      <div>
        {loading ? (
          <div>Loading webhooks...</div>
        ) : webhooks.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No webhooks found.</div>
        ) : (
          <table className="min-w-full bg-card rounded-lg shadow">
            <thead>
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">URL</th>
                <th className="text-left p-3">Created</th>
                <th className="text-left p-3">Status</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((wh) => (
                <tr key={wh.id} className="border-t">
                  <td className="p-3">{wh.name}</td>
                  <td className="p-3">{wh.url}</td>
                  <td className="p-3">{formatLocalDateTime(wh.createdAt)}</td>
                  <td className="p-3">{wh.isActive ? "Active" : "Inactive"}</td>
                  <td className="p-3 text-center flex gap-2 justify-center">
                    <Button variant="outline" size="sm" onClick={() => openEditModal(wh)}>
                      Edit
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => openHistoryModal(wh.id)}>
                      Delivery History
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(wh.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, webhook: open ? editModal.webhook : undefined })}>
        <DialogContent>
          <DialogTitle>Edit Webhook</DialogTitle>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="block mb-1 font-medium">Name</label>
              <Input name="name" value={editForm.name} onChange={handleEditInputChange} />
            </div>
            <div>
              <label className="block mb-1 font-medium">URL</label>
              <Input name="url" value={editForm.url} onChange={handleEditInputChange} />
            </div>
            <div>
              <label className="block mb-1 font-medium">Events</label>
              <div className="flex gap-8">
                {EVENT_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2">
                    <Checkbox
                      checked={editForm.eventTypes.includes(opt.value)}
                      onCheckedChange={() => handleEditCheckboxChange(opt.value)}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editForm.isActive} onCheckedChange={handleEditSwitchChange} />
              <span>{editForm.isActive ? "Active" : "Inactive"}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={() => setEditModal({ open: false })}>Cancel</Button>
              <Button type="submit" disabled={editSubmitting}>{editSubmitting ? "Saving..." : "Save Changes"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delivery History Modal */}
      <Dialog open={historyModal.open} onOpenChange={(open) => setHistoryModal({ open, webhookId: open ? historyModal.webhookId : undefined })}>
        <DialogContent>
          <DialogTitle>Delivery History</DialogTitle>
          {historyLoading ? (
            <div>Loading...</div>
          ) : deliveryHistory.length === 0 ? (
            <div>No deliveries found.</div>
          ) : (
            <table className="min-w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Response</th>
                  <th className="text-left p-2">Error</th>
                  <th className="text-left p-2">Attempted At</th>
                </tr>
              </thead>
              <tbody>
                {deliveryHistory.map((d) => (
                  <tr key={d.id} className="border-t">
                    <td className="p-2">{d.status}</td>
                    <td className="p-2">{d.responseCode ?? "-"}</td>
                    <td className="p-2">{d.errorMessage ?? "-"}</td>
                    <td className="p-2">{d.attemptedAt ? formatLocalDateTime(d.attemptedAt) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Webhooks; 